import api from '@/services/api';
import type { OutboxStatus, OutboxMessage, DeadLetter } from '@/types/integration';

const BASE_PATH = '/admin/integrations';

/**
 * Integration monitoring API service.
 *
 * Provides read access to the outbox message queue status,
 * individual messages, dead letters, and retry operations.
 */
export const integrationService = {
  /**
   * Fetches aggregate outbox status counts (pending, processing, completed, failed, dead letters).
   */
  async getOutboxStatus(): Promise<OutboxStatus> {
    const { data } = await api.get<OutboxStatus>(`${BASE_PATH}/outbox`);
    return data;
  },

  /**
   * Lists outbox messages, optionally filtered by status and/or message type.
   */
  async getOutboxMessages(params?: {
    status?: string;
    messageType?: string;
  }): Promise<OutboxMessage[]> {
    const { data } = await api.get<{ items: OutboxMessage[] }>(`${BASE_PATH}/outbox/messages`, {
      params,
    });
    return data.items;
  },

  /**
   * Retries a failed outbox message by ID.
   */
  async retryMessage(id: string): Promise<void> {
    await api.post(`${BASE_PATH}/outbox/${id}/retry`);
  },

  /**
   * Lists all dead-letter messages.
   */
  async getDeadLetters(): Promise<DeadLetter[]> {
    const { data } = await api.get<DeadLetter[]>(`${BASE_PATH}/dead-letters`);
    return data;
  },

  /**
   * Requeues a dead-letter message back into the outbox for retry.
   */
  async requeueDeadLetter(id: string): Promise<void> {
    await api.post(`${BASE_PATH}/dead-letters/${id}/requeue`);
  },
};
